import React, { useEffect, useRef, useState } from "react";
import useApi from "@/hooks/useApi";

interface Label {
  id: number;
  name: string;
  color: string;
  iconCode: string | null;
  sortOrder: number;
  isActive: boolean;
}

const PRESET_COLORS = [
  "#3B82F6", // blue
  "#10B981", // emerald
  "#F59E0B", // amber
  "#EF4444", // red
  "#8B5CF6", // violet
  "#06B6D4", // cyan
  "#F97316", // orange
  "#EC4899", // pink
  "#6B7280", // gray
  "#14B8A6", // teal
];

function LabelBadge({ name, color, iconCode }: { name: string; color: string; iconCode?: string | null }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium"
      style={{ backgroundColor: `${color}22`, color }}
    >
      {iconCode && <span>{iconCode}</span>}
      {name}
    </span>
  );
}

function EditLabelModal({
  label,
  onSave,
  onClose,
}: {
  label: Partial<Label> | null;
  onSave: (data: Partial<Label>) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(label?.name ?? "");
  const [color, setColor] = useState(label?.color ?? PRESET_COLORS[0]);
  const [icon, setIcon] = useState(label?.iconCode ?? "");
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => { nameRef.current?.focus(); }, []);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-[420px] p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4">
          {label?.id ? "Edit label" : "Create label"}
        </h3>

        {/* Preview */}
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg mb-5">
          <span className="text-xs text-slate-500">Preview:</span>
          <LabelBadge name={name || "My label"} color={color} iconCode={icon || null} />
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Label name <span className="text-rose-500">*</span></label>
            <input
              ref={nameRef}
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={100}
              placeholder="e.g. Billing, VIP, Bug"
              className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Color</label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  aria-label={`Color ${c}`}
                  className={`w-7 h-7 rounded-full transition-all ${color === c ? "ring-2 ring-offset-2 ring-slate-400 scale-110" : "hover:scale-105"}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <input
              type="text"
              value={color}
              onChange={e => setColor(e.target.value)}
              placeholder="#3B82F6"
              className="mt-2 text-xs border border-slate-200 rounded px-2 py-1 w-24 font-mono focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Icon / Emoji (optional)</label>
            <input
              type="text"
              value={icon}
              onChange={e => setIcon(e.target.value)}
              maxLength={10}
              placeholder="💼 or leave blank"
              className="w-36 text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-6 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => { if (name.trim()) onSave({ ...label, name: name.trim(), color, iconCode: icon.trim() || null }); }}
            disabled={!name.trim()}
            className="px-4 py-2 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:opacity-90 disabled:opacity-40 transition-colors"
          >
            {label?.id ? "Save changes" : "Create label"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ConversationLabelsPanel() {
  const api = useApi();
  const [labels, setLabels] = useState<Label[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingLabel, setEditingLabel] = useState<Partial<Label> | null | false>(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await api.admin.get("ConversationLabels?includeInactive=false");
        if (res.ok) setLabels(await res.json());
      } catch {}
      finally { setLoading(false); }
    }
    load();
  }, [api.admin]);

  async function handleSave(data: Partial<Label>) {
    setSaving(true);
    try {
      if (data.id) {
        const res = await api.admin.put(`ConversationLabels/${data.id}`, data);
        if (res.ok) {
          const updated = await res.json();
          setLabels(prev => prev.map(l => l.id === data.id ? { ...l, ...updated } : l));
        }
      } else {
        const res = await api.admin.post("ConversationLabels", data);
        if (res.ok) {
          const created = await res.json();
          setLabels(prev => [...prev, created]);
        }
      }
    } finally {
      setSaving(false);
      setEditingLabel(false);
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm("Remove this label? It will be hidden from agents but historical usage is preserved.")) return;
    try {
      const res = await api.admin.delete(`ConversationLabels/${id}`);
      if (res.ok) setLabels(prev => prev.filter(l => l.id !== id));
    } catch {}
  }

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Conversation Labels</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Agents apply labels to conversations to categorize them — Billing, VIP, Bug, Sales, etc.
            Labels make filtering, reporting, and handoffs fast.
          </p>
        </div>
        <button
          onClick={() => setEditingLabel({})}
          className="px-3 py-2 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:opacity-90 transition-colors shrink-0 ml-4"
        >
          + New label
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
      ) : labels.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-slate-200 rounded-xl">
          <div className="text-3xl mb-2">🏷️</div>
          <p className="text-slate-600 font-medium">No labels yet</p>
          <p className="text-sm text-slate-400 mt-1 mb-4">Create your first label to help agents categorize conversations.</p>
          <button
            onClick={() => setEditingLabel({})}
            className="px-4 py-2 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:opacity-90 transition-colors"
          >
            Create first label
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {labels.map(label => (
            <div
              key={label.id}
              className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors"
            >
              {/* Color swatch */}
              <div
                className="w-3 h-8 rounded-full shrink-0"
                style={{ backgroundColor: label.color }}
              />

              {/* Badge preview */}
              <LabelBadge name={label.name} color={label.color} iconCode={label.iconCode} />

              {/* Spacer */}
              <div className="flex-1" />

              {/* Actions */}
              <button
                onClick={() => setEditingLabel(label)}
                className="text-xs text-slate-500 hover:text-blue-600 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(label.id)}
                className="text-xs text-slate-400 hover:text-rose-600 px-2 py-1 rounded hover:bg-rose-50 transition-colors"
                aria-label="Remove label"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl">
        <p className="text-xs text-blue-800 font-medium mb-1">How labels work</p>
        <p className="text-xs text-blue-700 leading-relaxed">
          Agents apply labels during or after a conversation from the messaging workspace. Labels appear in conversation
          lists, reports, and contact timelines. Use them to route escalations, track trends, or identify VIP customers.
        </p>
      </div>

      {editingLabel !== false && (
        <EditLabelModal
          label={editingLabel}
          onSave={handleSave}
          onClose={() => setEditingLabel(false)}
        />
      )}
    </div>
  );
}
